import { Link, NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, Sun, Moon, Bell, Search, LayoutDashboard, User, Settings, MessageCircle, LogOut, Briefcase, Star, Coffee, Award, Heart, ShieldCheck, Users, Mail, ClipboardCheck, Sparkles, BarChart3, Megaphone } from "lucide-react";
import { WordMark } from "@/components/brand/Logo";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { api } from "@/lib/api";
import { TID } from "@/constants/testIds";

const authedLinks = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, tid: TID.nav.dashboard },
  { to: "/jobs", label: "Projects", icon: Briefcase, tid: TID.nav.jobs },
  { to: "/search", label: "Discover", icon: Search, tid: TID.nav.search },
  { to: "/messages", label: "Messages", icon: MessageCircle, tid: TID.nav.messages },
  { to: "/coffee-chats", label: "Coffee Chats", icon: Coffee, tid: "nav-coffee-chats" },
];

export function Navbar() {
  const { user, logout } = useAuth();
  const { prefs, setTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const navigate = useNavigate();

  const dark = prefs.theme === "dark";
  const toggleTheme = () => setTheme(dark ? "light" : "dark");

  const isAuthed = user && typeof user === "object";
  const isAdmin = isAuthed && (user.roles || []).includes("admin");
  const isReviewer = isAuthed && ((user.roles || []).includes("admin") || (user.roles || []).includes("moderator"));
  const hasWorkspace = isAuthed && ((user.roles || []).includes("author") || (user.roles || []).includes("beta_reader"));

  useEffect(() => {
    if (!isAuthed) return;
    let mounted = true;
    const load = async () => {
      try { const { data } = await api.get("/notifications/unread-count"); if (mounted) setUnread(data.unread || 0); }
      catch {}
    };
    load();
    const iv = setInterval(load, 30000);
    return () => { mounted = false; clearInterval(iv); };
  }, [isAuthed, user?.id]);

  const [certPending, setCertPending] = useState(0);
  useEffect(() => {
    if (!isReviewer) return;
    let mounted = true;
    const load = async () => {
      try {
        const { data } = await api.get("/certifications/admin/stats");
        if (mounted) setCertPending((data?.pending || 0) + (data?.more_info_requested || 0));
      } catch {}
    };
    load();
    const iv = setInterval(load, 60000);
    return () => { mounted = false; clearInterval(iv); };
  }, [isReviewer, user?.id]);

  // Subtle red dot on the admin "Private Tester Requests" nav entry
  // whenever there's at least one pending request. Polled once a minute
  // like certPending — light-touch, no runtime cost for non-admins.
  const [ptPending, setPtPending] = useState(0);
  useEffect(() => {
    if (!isAdmin) return;
    let mounted = true;
    const load = async () => {
      try {
        const { data } = await api.get("/admin/private-tester-requests");
        if (mounted) setPtPending((data?.pending || []).length);
      } catch {}
    };
    load();
    const iv = setInterval(load, 60000);
    return () => { mounted = false; clearInterval(iv); };
  }, [isAdmin, user?.id]);

  const linkClass = ({ isActive }) =>
    `inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-[15px] font-semibold transition-all ${
      isActive
        ? "bg-[hsl(var(--sb-emerald))] text-[hsl(40_60%_96%)] shadow-inner ring-1 ring-[hsl(var(--sb-gold))]/30"
        : "text-foreground/75 hover:text-[hsl(40_60%_96%)] hover:bg-[hsl(var(--sb-emerald))]/50"
    }`;

  return (
    <header className="glass-nav border-b border-[hsl(var(--sb-emerald))]/60">
      <div className="container-page flex h-[68px] items-center justify-between gap-2 min-w-0">
        <Link to="/" data-testid={TID.nav.logo} className="shrink min-w-0 max-w-[60%] md:max-w-none md:shrink-0">
          <WordMark />
        </Link>

        {/* Desktop links */}
        <nav className="hidden md:flex items-center gap-1">
          {isAuthed &&
            authedLinks.map(({ to, label, icon: Icon, tid }) => (
              <NavLink key={to} to={to} data-testid={tid} className={linkClass}>
                <Icon size={17} /> {label}
              </NavLink>
            ))}
        </nav>

        <div className="flex items-center gap-0.5 sm:gap-1.5 shrink-0">
          {isAuthed && (
            <Link to="/notifications" data-testid={TID.nav.notifications} className="relative rounded-full p-1.5 sm:p-2.5 text-foreground/70 hover:bg-muted hover:text-foreground transition-colors" aria-label="Notifications">
              <Bell size={18} />
              {unread > 0 && (
                <span className="absolute top-0.5 right-0.5 sm:top-1 sm:right-1 min-w-[16px] h-4 px-1 rounded-full bg-[hsl(var(--sb-clay))] text-white text-[10px] font-semibold grid place-items-center" data-testid="nav-notif-unread">
                  {unread > 9 ? "9+" : unread}
                </span>
              )}
            </Link>
          )}
          <button
            type="button"
            onClick={toggleTheme}
            data-testid={TID.nav.themeToggle}
            className="rounded-full p-1.5 sm:p-2.5 text-foreground/70 hover:bg-muted hover:text-foreground transition-colors"
            aria-label={dark ? "Switch to light mode" : "Switch to dark mode"}
            title={dark ? "Switch to light mode" : "Switch to dark mode"}
          >
            {dark ? <Sun size={18} /> : <Moon size={18} />}
          </button>

          {isAuthed ? (
            <div className="relative hidden md:block">
              <button
                type="button"
                onClick={() => setMenuOpen((v) => !v)}
                data-testid={TID.nav.userMenu}
                className="flex items-center gap-2 rounded-full pl-0.5 pr-0.5 sm:pl-1 sm:pr-3 py-1 hover:bg-muted transition-colors"
              >
                <Avatar user={user} size={30} />
                <span className="hidden sm:inline text-sm font-medium max-w-[9rem] truncate">
                  {user.profile?.display_name || user.email}
                </span>
              </button>
              {menuOpen && (
                <div
                  className="absolute right-0 mt-2 w-56 rounded-2xl border bg-card shadow-lg p-1.5 z-50"
                  onMouseLeave={() => setMenuOpen(false)}
                >
                  <MenuItem to="/profile" onClick={() => setMenuOpen(false)} icon={User} label="Profile" tid={TID.nav.profile} />
                  <MenuItem to="/dashboard" onClick={() => setMenuOpen(false)} icon={LayoutDashboard} label="Dashboard" tid={TID.nav.dashboard + "-menu"} />
                  {hasWorkspace && (
                    <MenuItem
                      to="/dashboard#my-activity"
                      onClick={() => setMenuOpen(false)}
                      icon={Sparkles}
                      label="Your Workspace"
                      tid="nav-your-workspace"
                    />
                  )}
                  <MenuItem to="/reviews" onClick={() => setMenuOpen(false)} icon={Star} label="My Reviews" tid="nav-my-reviews" />
                  <MenuItem to="/search" onClick={() => setMenuOpen(false)} icon={Search} label="Discover" tid={TID.nav.search + "-menu"} />
                  <MenuItem to="/favorites" onClick={() => setMenuOpen(false)} icon={Heart} label="Favorites" tid="nav-favorites" />
                  <MenuItem to="/community" onClick={() => setMenuOpen(false)} icon={Award} label="Quality & Certifications" tid="nav-community" />
                  <MenuItem to="/notifications" onClick={() => setMenuOpen(false)} icon={Bell} label="Notifications" tid={TID.nav.notifications + "-menu"} />
                  <MenuItem to="/settings" onClick={() => setMenuOpen(false)} icon={Settings} label="Settings" tid={TID.nav.settings} />
                  {isAdmin && (
                    <>
                      <MenuItem
                        to="/admin/analytics"
                        onClick={() => setMenuOpen(false)}
                        icon={BarChart3}
                        label="Analytics"
                        tid="nav-admin-analytics"
                      />
                      <MenuItem
                        to="/admin/users"
                        onClick={() => setMenuOpen(false)}
                        icon={Users}
                        label="Registered users"
                        tid="nav-admin-users"
                      />
                      <MenuItem
                        to="/admin/feedback"
                        onClick={() => setMenuOpen(false)}
                        icon={ShieldCheck}
                        label="Beta feedback"
                        tid="nav-admin-feedback"
                      />
                      <MenuItem
                        to="/admin/email-center"
                        onClick={() => setMenuOpen(false)}
                        icon={Mail}
                        label="Email Center"
                        tid="nav-admin-email-center"
                      />
                      <MenuItem
                        to="/admin/certifications"
                        onClick={() => setMenuOpen(false)}
                        icon={ClipboardCheck}
                        label="Certification review"
                        tid="nav-admin-certifications"
                        badge={certPending}
                      />
                      <MenuItem
                        to="/admin/acknowledgments"
                        onClick={() => setMenuOpen(false)}
                        icon={ShieldCheck}
                        label="Ack log"
                        tid="nav-admin-acknowledgments"
                      />
                      <MenuItem
                        to="/admin/community-updates"
                        onClick={() => setMenuOpen(false)}
                        icon={Megaphone}
                        label="Community Updates"
                        tid="nav-admin-community-updates"
                      />
                      <MenuItem
                        to="/admin/private-tester-requests"
                        onClick={() => setMenuOpen(false)}
                        icon={ShieldCheck}
                        label="Private Tester Requests"
                        tid="nav-admin-private-tester-requests"
                        dot={ptPending > 0}
                      />
                    </>
                  )}
                  <button
                    type="button"
                    data-testid={TID.nav.logout}
                    onClick={async () => {
                      setMenuOpen(false);
                      await logout();
                      navigate("/");
                    }}
                    className="mt-1 flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm text-destructive hover:bg-destructive/10"
                  >
                    <LogOut size={16} /> Sign out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="hidden sm:flex items-center gap-2">
              <Link to="/login" data-testid={TID.nav.login} className="btn-ghost">
                Sign in
              </Link>
              <Link to="/register" data-testid={TID.nav.signup} className="btn-primary">
                Join Last Bookmark
              </Link>
            </div>
          )}

          <button
            type="button"
            className="md:hidden relative rounded-full p-1.5 sm:p-2.5 hover:bg-muted"
            onClick={() => setOpen((v) => !v)}
            data-testid={TID.nav.mobileToggle}
            aria-label="Open menu"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
            {isAdmin && ptPending > 0 && !open && (
              <span
                className="absolute top-1 right-1 w-2 h-2 rounded-full bg-[hsl(var(--sb-clay))] shadow-[0_0_0_2px_hsl(var(--background))]"
                data-testid="nav-mobile-toggle-dot"
                aria-label="Pending admin items"
              />
            )}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          className="md:hidden border-t bg-background max-h-[calc(100vh-4rem)] overflow-y-auto overscroll-contain"
          data-testid="nav-mobile-panel"
        >
          <div className="container-page py-4 pb-[max(env(safe-area-inset-bottom),1.5rem)] flex flex-col gap-1">
            {isAuthed ? (
              <>
                <div className="flex items-center gap-3 rounded-2xl border bg-muted/40 p-3 mb-2">
                  <Avatar user={user} size={40} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold truncate">
                      {user.profile?.display_name || user.email}
                    </div>
                    {user.profile?.display_name && (
                      <div className="text-xs text-muted-foreground truncate">{user.email}</div>
                    )}
                  </div>
                  <button
                    type="button"
                    data-testid={TID.nav.logout + "-mobile-top"}
                    onClick={async () => {
                      setOpen(false);
                      await logout();
                      navigate("/");
                    }}
                    className="shrink-0 inline-flex items-center gap-1.5 rounded-full border border-destructive/30 px-3 py-1.5 text-xs font-medium text-destructive hover:bg-destructive/10 transition-colors"
                    aria-label="Sign out"
                  >
                    <LogOut size={14} /> Sign out
                  </button>
                </div>
                {authedLinks.map(({ to, label, icon: Icon, tid }) => (
                  <NavLink
                    key={to}
                    to={to}
                    data-testid={tid + "-mobile"}
                    onClick={() => setOpen(false)}
                    className={linkClass}
                  >
                    <Icon size={16} /> {label}
                  </NavLink>
                ))}
                {hasWorkspace && (
                  <NavLink
                    to="/dashboard#my-activity"
                    onClick={() => setOpen(false)}
                    className={linkClass}
                    data-testid="nav-your-workspace-mobile"
                  >
                    <Sparkles size={16} /> Your Workspace
                  </NavLink>
                )}
                <div className="my-2 border-t border-border/70" />
                <NavLink to="/profile" onClick={() => setOpen(false)} className={linkClass} data-testid={TID.nav.profile + "-mobile"}>
                  <User size={16} /> Profile
                </NavLink>
                <NavLink to="/reviews" onClick={() => setOpen(false)} className={linkClass} data-testid="nav-my-reviews-mobile">
                  <Star size={16} /> My Reviews
                </NavLink>
                <NavLink to="/favorites" onClick={() => setOpen(false)} className={linkClass} data-testid="nav-favorites-mobile">
                  <Heart size={16} /> Favorites
                </NavLink>
                <NavLink to="/community" onClick={() => setOpen(false)} className={linkClass} data-testid="nav-community-mobile">
                  <Award size={16} /> Quality & Certifications
                </NavLink>
                <NavLink to="/notifications" onClick={() => setOpen(false)} className={linkClass} data-testid={TID.nav.notifications + "-menu-mobile"}>
                  <Bell size={16} /> Notifications
                  {unread > 0 && (
                    <span className="ml-auto min-w-[18px] h-5 px-1.5 rounded-full bg-[hsl(var(--sb-clay))] text-white text-[11px] font-semibold grid place-items-center">
                      {unread > 9 ? "9+" : unread}
                    </span>
                  )}
                </NavLink>
                <NavLink to="/settings" onClick={() => setOpen(false)} className={linkClass} data-testid={TID.nav.settings + "-mobile"}>
                  <Settings size={16} /> Settings
                </NavLink>
                {isAdmin && (
                  <>
                    <div className="mt-2 mb-1 px-2 text-[10px] uppercase tracking-[0.14em] font-semibold text-muted-foreground">Admin</div>
                    <NavLink to="/admin/analytics" onClick={() => setOpen(false)} className={linkClass} data-testid="nav-admin-analytics-mobile">
                      <BarChart3 size={16} /> Analytics
                    </NavLink>
                    <NavLink to="/admin/users" onClick={() => setOpen(false)} className={linkClass} data-testid="nav-admin-users-mobile">
                      <Users size={16} /> Registered users
                    </NavLink>
                    <NavLink to="/admin/feedback" onClick={() => setOpen(false)} className={linkClass} data-testid="nav-admin-feedback-mobile">
                      <ShieldCheck size={16} /> Beta feedback
                    </NavLink>
                    <NavLink to="/admin/email-center" onClick={() => setOpen(false)} className={linkClass} data-testid="nav-admin-email-center-mobile">
                      <Mail size={16} /> Email Center
                    </NavLink>
                    <NavLink to="/admin/certifications" onClick={() => setOpen(false)} className={linkClass} data-testid="nav-admin-certifications-mobile">
                      <ClipboardCheck size={16} />
                      <span className="flex-1">Certification review</span>
                      {certPending > 0 && (
                        <span
                          className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[hsl(var(--sb-clay))] text-white text-[11px] font-semibold"
                          data-testid="nav-admin-certifications-mobile-badge"
                        >
                          {certPending > 99 ? "99+" : certPending}
                        </span>
                      )}
                    </NavLink>
                    <NavLink to="/admin/acknowledgments" onClick={() => setOpen(false)} className={linkClass} data-testid="nav-admin-acknowledgments-mobile">
                      <ShieldCheck size={16} /> Ack log
                    </NavLink>
                    <NavLink to="/admin/community-updates" onClick={() => setOpen(false)} className={linkClass} data-testid="nav-admin-community-updates-mobile">
                      <Megaphone size={16} /> Community Updates
                    </NavLink>
                    <NavLink to="/admin/private-tester-requests" onClick={() => setOpen(false)} className={linkClass} data-testid="nav-admin-private-tester-requests-mobile">
                      <ShieldCheck size={16} /> Private Tester Requests
                      {ptPending > 0 && (
                        <span
                          className="inline-block w-2 h-2 rounded-full bg-[hsl(var(--sb-clay))] ml-1 shadow-[0_0_0_2px_hsl(var(--background))]"
                          data-testid="nav-admin-private-tester-requests-mobile-dot"
                          aria-label="Pending items"
                        />
                      )}
                    </NavLink>
                  </>
                )}
                <button
                  type="button"
                  data-testid={TID.nav.logout + "-mobile"}
                  onClick={async () => {
                    setOpen(false);
                    await logout();
                    navigate("/");
                  }}
                  className="mt-3 mb-2 flex items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm font-medium text-destructive border border-destructive/30 hover:bg-destructive/10 transition-colors"
                >
                  <LogOut size={16} /> Sign out
                </button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)} className="btn-ghost justify-start" data-testid={TID.nav.login + "-mobile"}>Sign in</Link>
                <Link to="/register" onClick={() => setOpen(false)} className="btn-primary justify-start" data-testid={TID.nav.signup + "-mobile"}>Join Last Bookmark</Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}

function MenuItem({ to, icon: Icon, label, onClick, tid, badge, dot }) {
  return (
    <Link
      to={to}
      onClick={onClick}
      data-testid={tid}
      className="flex items-center gap-2 rounded-xl px-3 py-2 text-sm hover:bg-muted"
    >
      <Icon size={16} />
      <span className="flex-1 inline-flex items-center gap-1.5">
        {label}
        {dot && !badge && (
          <span
            className="inline-block w-2 h-2 rounded-full bg-[hsl(var(--sb-clay))] shadow-[0_0_0_2px_hsl(var(--background))]"
            data-testid={`${tid}-dot`}
            aria-label="Pending items"
          />
        )}
      </span>
      {badge != null && badge > 0 && (
        <span
          className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[hsl(var(--sb-clay))] text-white text-[11px] font-semibold"
          data-testid={`${tid}-badge`}
        >
          {badge > 99 ? "99+" : badge}
        </span>
      )}
    </Link>
  );
}

export function Avatar({ user, size = 36 }) {
  const url = user?.profile?.avatar_url;
  const name = (user?.profile?.display_name || user?.email || "?").trim();
  const initial = name[0]?.toUpperCase() || "S";
  const style = { width: size, height: size };
  if (url) {
    return (
      <img
        src={url}
        style={style}
        alt={name}
        className="rounded-full object-cover ring-2 ring-background"
      />
    );
  }
  return (
    <div
      style={style}
      className="rounded-full grid place-items-center bg-[hsl(var(--sb-moss))] text-[hsl(var(--sb-forest-2))] font-serif font-semibold ring-2 ring-background"
    >
      {initial}
    </div>
  );
}
